import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Mic, Square, Play, Pause, RotateCcw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const VoiceRecorder = ({ onRecordingComplete, t }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const { toast } = useToast();

  const MAX_DURATION = 60; // 60 seconds

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioURL) URL.revokeObjectURL(audioURL);
    };
  }, [audioURL]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        setAudioBlob(audioBlob);
        onRecordingComplete(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        toast({
          title: t('form.voiceRecordSuccess'),
          description: "You can listen to it or re-record if needed.",
        });
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= MAX_DURATION) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast({
        title: "Microphone Access Denied",
        description: "Please allow microphone access to record your voice.",
        variant: "destructive"
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const playRecording = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const reRecord = () => {
    if (audioURL) URL.revokeObjectURL(audioURL);
    setAudioURL(null);
    setAudioBlob(null);
    setRecordingTime(0);
    setIsPlaying(false);
    onRecordingComplete(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Instructions */}
      {!audioURL && !isRecording && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-900">
            <strong>🎤 {t('form.voiceRecordInstructions')}</strong>
          </p>
          <p className="text-xs text-blue-700 mt-1">
            {t('form.voiceRecordMaxDuration')}
          </p>
        </div>
      )}

      {/* Recording Interface */}
      <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50/50">
        {/* Microphone Visualization */}
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-all ${
          isRecording 
            ? 'bg-red-500 animate-pulse' 
            : audioURL 
            ? 'bg-green-500' 
            : 'bg-blue-500'
        }`}>
          {isRecording ? (
            <Mic className="w-10 h-10 text-white" />
          ) : audioURL ? (
            <Play className="w-10 h-10 text-white" />
          ) : (
            <Mic className="w-10 h-10 text-white" />
          )}
        </div>

        {/* Timer */}
        <div className="text-2xl font-bold text-gray-900 mb-4">
          {isRecording ? (
            <span className="text-red-600">{formatTime(recordingTime)}</span>
          ) : audioURL ? (
            <span className="text-green-600">✓ {formatTime(recordingTime)}</span>
          ) : (
            <span className="text-gray-400">0:00</span>
          )}
        </div>

        {/* Controls */}
        <div className="flex gap-3">
          {!isRecording && !audioURL && (
            <Button
              type="button"
              onClick={startRecording}
              className="bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 hover:from-blue-700 hover:to-cyan-600 text-white"
            >
              <Mic className="w-4 h-4 mr-2" />
              {t('form.voiceRecordStart')}
            </Button>
          )}

          {isRecording && (
            <Button
              type="button"
              onClick={stopRecording}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Square className="w-4 h-4 mr-2" />
              {t('form.voiceRecordStop')}
            </Button>
          )}

          {audioURL && (
            <>
              <Button
                type="button"
                onClick={playRecording}
                variant="outline"
                className="border-green-500 text-green-700 hover:bg-green-50"
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-4 h-4 mr-2" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    {t('form.voiceRecordPlay')}
                  </>
                )}
              </Button>
              <Button
                type="button"
                onClick={reRecord}
                variant="outline"
                className="border-purple-500 text-blue-700 hover:bg-blue-50"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t('form.voiceRecordReRecord')}
              </Button>
            </>
          )}
        </div>

        {/* Progress Bar */}
        {isRecording && (
          <div className="w-full mt-4">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-red-500 to-pink-500 transition-all duration-1000"
                style={{ width: `${(recordingTime / MAX_DURATION) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Hidden Audio Element */}
        {audioURL && (
          <audio
            ref={audioRef}
            src={audioURL}
            onEnded={() => setIsPlaying(false)}
            className="hidden"
          />
        )}
      </div>

      {/* Success Message */}
      {audioURL && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-900">
            ✓ <strong>Voice recorded!</strong> Your voice will be integrated into the song.
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
